// Faixa Unicode dos diacríticos combinantes (acentos U+0300–U+036F), removida após NFD.
// Construído via fromCharCode pra manter o fonte 100% ASCII (sem marcas combinantes invisíveis).
const COMBINING_MARKS_REGEX = new RegExp(
    `[${String.fromCharCode(0x300)}-${String.fromCharCode(0x36f)}]`,
    'g',
);

/**
 * Normaliza texto pra busca: tira acento (NFD + remove diacríticos), minúsculas e trim.
 * Assim "Água", "agua" e "AGUA" casam entre si. Mantém o resto (pontuação, números) —
 * é uma normalização gentil, ao contrário de normalizeAutoCategorizationText que remove
 * ruído/pontuação e serve pra casar regras de categorização.
 */
export function normalizeSearchText(value: string | null | undefined): string {
    if (!value) return '';
    return value.normalize('NFD').replace(COMBINING_MARKS_REGEX, '').toLowerCase().trim();
}

/**
 * O termo da busca lido como valor: os reais inteiros e o começo dos centavos que a pessoa
 * digitou. "150" = 150,00 a 150,99; "89,9" = 89,90 a 89,99; "89,90" = só 89,90.
 */
export interface AmountQuery {
    reais: number;
    /** Os centavos digitados, de '' a 2 dígitos ("150," e "150" dão ''). */
    cents: string;
}

// Número com moeda e sinal opcionais. Ponto e vírgula entram nos dois papéis: o Kontto
// tem moedas com "1.500,00" e com "1,500.00".
const AMOUNT_QUERY_REGEX = /^[+-]?\s*(?:r\$|us\$|\$|€|£)?\s*[+-]?\s*(\d[\d.,]*)$/;

/**
 * Lê o termo da busca como valor; `null` quando não parece número (aí a busca é só texto).
 *
 * O separador decide pelo que vem depois do ÚLTIMO dele: 3 dígitos é milhar ("1.500" e
 * "1,500" = 1500), 1 ou 2 são centavos ("89,90" e "89.90"), nenhum é a pessoa no meio da
 * digitação ("150," = 150).
 *
 * Os reais casam inteiros e os centavos pelo começo, para a lista estreitar enquanto se
 * digita: "15" não traz 150,00 nem 1.150,00, que era o que "contém 15" traria.
 */
export function parseAmountQuery(query: string): AmountQuery | null {
    const match = query.trim().toLowerCase().match(AMOUNT_QUERY_REGEX);
    if (!match) return null;

    const number = match[1];
    const lastSeparator = Math.max(number.lastIndexOf(','), number.lastIndexOf('.'));
    if (lastSeparator < 0) return { reais: Number(number), cents: '' };

    const after = number.slice(lastSeparator + 1);
    if (after.length === 3) return { reais: Number(number.replace(/[.,]/g, '')), cents: '' };
    if (after.length > 2) return null;

    return { reais: Number(number.slice(0, lastSeparator).replace(/[.,]/g, '')), cents: after };
}

/** O valor (com ou sem sinal) é o que a busca leu do termo. */
export function matchesAmountQuery(amount: number | null | undefined, query: AmountQuery): boolean {
    if (amount == null || !Number.isFinite(amount)) return false;

    const totalCents = Math.round(Math.abs(amount) * 100);
    if (Math.floor(totalCents / 100) !== query.reais) return false;
    return String(totalCents % 100).padStart(2, '0').startsWith(query.cents);
}
